import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Users, HelpCircle, BarChart3, X, Send, Plus, Edit, Trash2, AlertCircle, PhoneCall, Play, Pause } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { KnowledgeModal }           from './bot/KnowledgeModal';
import { AdminChatModal }           from './bot/AdminChatModal';
import { DirectInterventionModal }  from './bot/DirectInterventionModal';
import { InterventionModal }        from './bot/InterventionModal';
import { ConversationModal }        from './bot/ConversationModal';
import { AgentModal }               from './bot/AgentModal';

const BotManagementComplete = () => {
  // États principaux
  const [activeTab, setActiveTab] = useState('conversations');
  const [conversations, setConversations] = useState([]);
  const [agents, setAgents] = useState({});
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null); // null = ajout, {id, data} = édition
  const [editingKnowledge, setEditingKnowledge] = useState(null); // null = ajout, item = édition
  
  // Système d'alertes (IDENTIQUE à AdminQuotes)
  const [lastNotificationCount, setLastNotificationCount] = useState(0);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const audioContextRef = useRef(null);
  const soundIntervalRef = useRef(null);
  
  // Modals
  const [showKnowledgeModal, setShowKnowledgeModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showAdminChatModal, setShowAdminChatModal] = useState(false);
  const [showInterventionModal, setShowInterventionModal] = useState(false);
  const [showDirectInterventionModal, setShowDirectInterventionModal] = useState(false);
  
  const API_URL = window.location.origin;
  
  // Polling initial
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, []);
  
  // SYSTÈME D'ALERTES
  const agentRequestedRef = useRef(false); // Ref pour éviter les alertes vocales répétées

  useEffect(() => {
    const newConvs = conversations.filter(c => c.isNew === true);
    const total = newConvs.length;

    // Demandes d'agent urgentes non consultées
    const urgentConvs = conversations.filter(c => (c.agent_requested === true || c.urgent === true) && !c.agent_requested_seen);

    if (urgentConvs.length > 0) {
      // Première détection → voix immédiate + beep
      if (!agentRequestedRef.current) {
        agentRequestedRef.current = true;
        speakAgentAlert();
        playBotNotificationSound();
      }
      // Démarrer l'intervalle voix répétitif (toutes les 20s) jusqu'à consultation
      if (!soundIntervalRef.current) {
        soundIntervalRef.current = setInterval(() => {
          speakAgentAlert();
          playBotNotificationSound();
        }, 20000);
      }
      setHasNewNotification(true);
      if (total > lastNotificationCount) setLastNotificationCount(total);
    } else {
      // Plus d'urgences → tout stopper immédiatement
      agentRequestedRef.current = false;
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
      }
      // Gérer les nouvelles convs non urgentes
      if (total > lastNotificationCount && total > 0) {
        setHasNewNotification(true);
        setLastNotificationCount(total);
        playBotNotificationSound();
      } else if (total === 0) {
        setHasNewNotification(false);
        setLastNotificationCount(0);
      } else if (total < lastNotificationCount) {
        setLastNotificationCount(total);
        if (total === 0) setHasNewNotification(false);
      }
    }
  }, [conversations, lastNotificationCount]);
  
  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    
    try {
      // Conversations
      const convResp = await fetch(`${API_URL}/api/chat/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (convResp.ok) {
        const data = await convResp.json();
        setConversations(data);
      }
      
      // Agents
      const agentsResp = await fetch(`${API_URL}/api/chat/agents/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (agentsResp.ok) {
        const data = await agentsResp.json();
        setAgents(data.agents || {});
      }
      
      // Stats
      const statsResp = await fetch(`${API_URL}/api/chat/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsResp.ok) {
        const data = await statsResp.json();
        setStats(data);
      }
      
      // Knowledge Base
      const kbResp = await fetch(`${API_URL}/api/chat/knowledge-base`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (kbResp.ok) {
        const data = await kbResp.json();
        console.log('📚 Knowledge Base chargée:', data.length, 'entrées');
        if (data.length > 0) {
          console.log('Premier élément:', data[0]);
        }
        setKnowledgeBase(data);
      }
      
    } catch (error) {
      console.error('Erreur chargement données bot:', error);
    }
  };
  
  // Son robotique (différent de AdminQuotes)
  const playBotNotificationSound = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const audioContext = audioContextRef.current;
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      const frequencies = [400, 550, 700];
      frequencies.forEach((freq, i) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(audioContext.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        const startTime = audioContext.currentTime + (i * 0.2);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.5, startTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);
        osc.start(startTime);
        osc.stop(startTime + 0.2);
      });
    } catch (error) {
      console.error('Erreur son:', error);
    }
  };

  // Alerte vocale "Un client demande un agent"
  const speakAgentAlert = () => {
    try {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance('Un client demande un agent. Veuillez intervenir.');
        utterance.lang = 'fr-FR';
        utterance.rate = 0.85;
        utterance.volume = 1;
        utterance.pitch = 1;
        window.speechSynthesis.speak(utterance);
      }
    } catch (e) {
      console.error('Erreur alerte vocale:', e);
    }
  };
  
  // Arrêter les alertes (comme AdminQuotes)
  const stopNotificationLoop = async () => {
    // Arrêter l'intervalle de son immédiatement
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
      console.log('🔇 Boucle sonore arrêtée');
    }
    
    // Mettre isNew à false AVANT de reset le compteur (évite race condition)
    setHasNewNotification(false);
    
    // Acquitter sur le serveur EN PREMIER
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/chat/acknowledge-bot-notifications`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        console.log('✅ Notifications Bot IA acquittées sur le serveur');
        // Recharger les données (maintenant isNew=false dans la DB)
        await fetchData();
        // Seulement APRÈS le rechargement, reset le compteur
        setLastNotificationCount(0);
      }
    } catch (error) {
      console.error('Erreur acquittement:', error);
      setLastNotificationCount(0); // Reset de sécurité en cas d'erreur
    }
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'conversations' && hasNewNotification) {
      stopNotificationLoop();
    }
  };
  
  return (
    <div className="p-6">
      {/* Header avec bouton stop alerte */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">🤖 Gestion du Bot IA</h1>
          <p className="text-gray-600">Gérez votre assistant virtuel PhoneLab</p>
        </div>
        
        {hasNewNotification && (
          <button
            onClick={stopNotificationLoop}
            className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold shadow-lg animate-pulse"
          >
            <Pause size={20} />
            <span>Arrêter les alertes</span>
          </button>
        )}

        {/* Badge urgent 🚨 Agent demandé — seulement les non consultés */}
        {conversations.filter(c => (c.agent_requested || c.urgent) && !c.agent_requested_seen).length > 0 && (
          <div className="flex items-center space-x-2 bg-red-600 text-white px-4 py-3 rounded-lg font-bold shadow-xl animate-pulse border-2 border-red-300">
            <span className="text-xl">🚨</span>
            <div>
              <p className="text-sm font-bold leading-tight">AGENT DEMANDÉ</p>
              <p className="text-xs opacity-90">
                {conversations.filter(c => (c.agent_requested || c.urgent) && !c.agent_requested_seen).length} client(s) attend(ent)
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Tabs avec Badge */}
      <div className="flex space-x-2 mb-6 border-b">
        <button
          onClick={() => handleTabChange('conversations')}
          className={`px-4 py-2 font-semibold relative ${
            activeTab === 'conversations'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          📜 Conversations
          {hasNewNotification && lastNotificationCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
              {lastNotificationCount}
            </span>
          )}
        </button>
        
        <button
          onClick={() => setActiveTab('agents')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'agents'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          👥 Agents
        </button>
        
        <button
          onClick={() => setActiveTab('knowledge')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'knowledge'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          📚 Base de connaissances
        </button>
        
        <button
          onClick={() => setActiveTab('stats')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'stats'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          📊 Statistiques
        </button>
        
        <button
          onClick={() => setShowAdminChatModal(true)}
          className="px-4 py-2 font-semibold text-green-600 hover:bg-green-50 rounded-lg ml-auto"
        >
          💬 Communiquer avec le Bot
        </button>
      </div>
      
      {/* Content Tabs */}
      {activeTab === 'conversations' && (
        <ConversationsTab 
          conversations={conversations}
          setSelectedConversation={setSelectedConversation}
          setShowInterventionModal={setShowInterventionModal}
          setShowDirectInterventionModal={setShowDirectInterventionModal}
          fetchData={fetchData}
        />
      )}
      
      {activeTab === 'agents' && (
        <AgentsTab 
          agents={agents} 
          setShowAgentModal={setShowAgentModal}
          setEditingAgent={setEditingAgent}
          fetchData={fetchData}
          apiUrl={API_URL}
        />
      )}
      
      {activeTab === 'knowledge' && (
        <KnowledgeBaseTab
          knowledgeBase={knowledgeBase}
          setShowKnowledgeModal={setShowKnowledgeModal}
          setEditingKnowledge={setEditingKnowledge}
          fetchData={fetchData}
        />
      )}
      
      {activeTab === 'stats' && <StatsTab stats={stats} />}
      
      {/* Modals */}
      {showKnowledgeModal && (
        <KnowledgeModal
          item={editingKnowledge}
          onClose={() => { setShowKnowledgeModal(false); setEditingKnowledge(null); }}
          onSave={() => { fetchData(); setShowKnowledgeModal(false); setEditingKnowledge(null); }}
        />
      )}
      
      {showAgentModal && (
        <AgentModal
          agent={editingAgent}
          onClose={() => { setShowAgentModal(false); setEditingAgent(null); }}
          onSave={() => { fetchData(); setShowAgentModal(false); setEditingAgent(null); }}
          apiUrl={API_URL}
        />
      )}
      
      {showAdminChatModal && (
        <AdminChatModal onClose={() => setShowAdminChatModal(false)} />
      )}
      
      {showInterventionModal && selectedConversation && (
        <InterventionModal 
          conversation={selectedConversation}
          agents={agents}
          onClose={() => setShowInterventionModal(false)}
          onSave={fetchData}
        />
      )}
      
      {showDirectInterventionModal && selectedConversation && (
        <DirectInterventionModal
          conversation={selectedConversation}
          onClose={() => setShowDirectInterventionModal(false)}
          onSave={fetchData}
        />
      )}
      
      {selectedConversation && !showInterventionModal && !showDirectInterventionModal && (
        <ConversationModal
          conversation={selectedConversation}
          onClose={() => setSelectedConversation(null)}
          onInterveneAgent={() => setShowInterventionModal(true)}
          onInterveneDirect={() => setShowDirectInterventionModal(true)}
        />
      )}
    </div>
  );
};

// ============ TAB COMPONENTS ============

const ConversationsTab = ({ conversations, setSelectedConversation, setShowInterventionModal, setShowDirectInterventionModal, fetchData }) => {
  const [showArchived, setShowArchived] = useState(false);
  const [archivedConvs, setArchivedConvs] = useState([]);
  const [archiving, setArchiving] = useState(null); // session_id en cours d'archivage

  const markSeen = async (sessionId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${window.location.origin}/api/chat/conversations/${sessionId}/seen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (e) { /* silencieux */ }
  };

  const archiveConversation = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('Archiver cette conversation ? Elle sera retirée de la liste active.')) return;
    setArchiving(sessionId);
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${window.location.origin}/api/chat/conversations/${sessionId}/archive`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (e) {
      alert('Erreur lors de l\'archivage');
    } finally {
      setArchiving(null);
    }
  };

  const deleteConversation = async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('⚠️ Supprimer définitivement cette conversation ? Cette action est irréversible.')) return;
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(`${window.location.origin}/api/chat/conversations/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        fetchData();
      } else {
        alert('❌ Erreur lors de la suppression');
      }
    } catch (e) {
      alert('❌ Erreur réseau');
    }
  };

  const loadArchived = async () => {
    if (showArchived) { setShowArchived(false); return; }
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(`${window.location.origin}/api/chat/conversations?include_archived=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const all = await resp.json();
        setArchivedConvs(all.filter(c => c.archived === true));
        setShowArchived(true);
      }
    } catch (e) { alert('Erreur chargement archives'); }
  };

  const displayList = showArchived ? archivedConvs : conversations;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-2xl font-bold">📜 Conversations actives</h3>
        <div className="flex space-x-2">
          <button
            onClick={loadArchived}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${showArchived ? 'bg-gray-200 text-gray-700 border-gray-400' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
          >
            {showArchived ? '← Actives' : '🗄️ Archives'}
          </button>
          <button
            onClick={() => {
              const token = localStorage.getItem('admin_token');
              const url = `${window.location.origin}/api/chat/conversations/export/csv`;
              const a = document.createElement('a');
              a.href = url;
              a.download = 'conversations_phonelab.csv';
              // Fetch avec auth pour téléchargement sécurisé
              fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
                .then(r => r.blob())
                .then(blob => {
                  const burl = URL.createObjectURL(blob);
                  a.href = burl; a.click(); URL.revokeObjectURL(burl);
                });
            }}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1"
            data-testid="export-csv-btn"
          >
            📥 CSV
          </button>
          <button
            onClick={fetchData}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            🔄 Actualiser
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('⚠️ ATTENTION — Supprimer définitivement TOUTES les conversations ? Action irréversible.')) return;
              if (!window.confirm('Dernière confirmation : supprimer TOUTES les conversations définitivement ?')) return;
              const token = localStorage.getItem('admin_token');
              try {
                const resp = await fetch(`${window.location.origin}/api/chat/conversations/bulk/all`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (resp.ok) {
                  const data = await resp.json();
                  alert(`✅ ${data.message}`);
                  fetchData();
                } else {
                  alert('❌ Erreur lors de la suppression');
                }
              } catch (e) {
                alert('❌ Erreur réseau');
              }
            }}
            className="bg-red-800 hover:bg-red-900 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1"
            data-testid="delete-all-convs-btn"
            title="Supprimer définitivement toutes les conversations"
          >
            🗑️ Tout supprimer
          </button>
        </div>
      </div>

      {showArchived && (
        <p className="text-xs text-gray-400 italic">Affichage des conversations archivées ({archivedConvs.length})</p>
      )}

      <div className="grid gap-4">
        {displayList.map((conv) => {
          const userData = conv.user_data || {};
          const clientName = userData.name || 'Client Anonyme';
          const clientPhone = userData.phone || '';
          const deviceModel = userData.device_model || '';
          const title = `${clientName}${clientPhone ? ' - ' + clientPhone : ''}${deviceModel ? ' - ' + deviceModel : ''}`;

          // "AGENT DEMANDÉ" seulement si pas encore consulté
          const isUrgentUnseen = (conv.agent_requested || conv.urgent) && !conv.agent_requested_seen;
          const isNew = conv.isNew && !isUrgentUnseen;

          return (
            <Card
              key={conv.session_id}
              data-testid="conv-card"
              className={`hover:shadow-lg transition ${
                isUrgentUnseen
                  ? 'border-2 border-red-600 bg-red-50 shadow-red-200 shadow-md'
                  : isNew ? 'border-2 border-orange-400 bg-orange-50' : 'border border-gray-200'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start gap-3">
                  {/* Zone cliquable — marque comme vu */}
                  <div
                    className="flex-1 cursor-pointer min-w-0"
                    onClick={() => {
                      setSelectedConversation(conv);
                      markSeen(conv.session_id);
                    }}
                  >
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <MessageCircle className="text-blue-600 flex-shrink-0" size={18} />
                      <span className="font-semibold truncate">{title}</span>
                      {isUrgentUnseen && (
                        <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full animate-pulse font-bold flex-shrink-0">
                          🚨 AGENT DEMANDÉ
                        </span>
                      )}
                      {isNew && (
                        <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse flex-shrink-0">
                          🆕 NOUVEAU
                        </span>
                      )}
                      {conv.agent_requested_seen && (conv.agent_requested || conv.urgent) && (
                        <span className="bg-gray-400 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                          ✅ Consulté
                        </span>
                      )}
                      {conv.archived && (
                        <span className="bg-gray-300 text-gray-600 text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                          🗄️ Archivé
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {conv.messages?.[conv.messages.length - 1]?.content?.substring(0, 150) || '...'}
                    </p>

                    <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                      <span>💬 {conv.messages?.length || 0} messages</span>
                      <span>⏰ {new Date(conv.created_at).toLocaleString('fr-FR')}</span>
                      {conv.archived_at && (
                        <span>🗄️ {new Date(conv.archived_at).toLocaleDateString('fr-FR')}</span>
                      )}
                    </div>
                  </div>

                  {/* Boutons action */}
                  {!conv.archived && (
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedConversation(conv); setShowDirectInterventionModal(true); markSeen(conv.session_id); }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs whitespace-nowrap"
                        data-testid="intervenir-btn"
                      >
                        💬 Intervenir
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedConversation(conv); setShowInterventionModal(true); markSeen(conv.session_id); }}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-xs whitespace-nowrap"
                        data-testid="notifier-agent-btn"
                      >
                        📱 Notifier agent
                      </button>
                      <button
                        onClick={(e) => archiveConversation(e, conv.session_id)}
                        disabled={archiving === conv.session_id}
                        className="bg-gray-500 hover:bg-red-600 disabled:bg-gray-300 text-white px-3 py-1 rounded text-xs whitespace-nowrap transition-colors"
                        data-testid="fin-discussion-btn"
                        title="Archiver et terminer cette conversation"
                      >
                        {archiving === conv.session_id ? '...' : '🔚 Fin de discussion'}
                      </button>
                      <button
                        onClick={(e) => deleteConversation(e, conv.session_id)}
                        className="bg-red-800 hover:bg-red-900 text-white px-3 py-1 rounded text-xs whitespace-nowrap transition-colors"
                        data-testid="supprimer-conv-btn"
                        title="Supprimer définitivement"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {displayList.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            <MessageCircle size={64} className="mx-auto mb-4 opacity-30" />
            <p>{showArchived ? 'Aucune conversation archivée' : 'Aucune conversation active'}</p>
            {!showArchived && <p className="text-sm mt-1 text-gray-400">Les conversations archivées sont accessibles via "Archives"</p>}
          </div>
        )}
      </div>
    </div>
  );
};

const AgentsTab = ({ agents, setShowAgentModal, setEditingAgent, fetchData, apiUrl }) => {
  const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
  
  const deleteAgent = async (agentId, agentName) => {
    if (!window.confirm(`Supprimer l'agent ${agentName} ?`)) return;
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(`${apiUrl}/api/chat/agents/${agentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        fetchData();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (e) {
      alert('Erreur réseau');
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-2xl font-bold">👥 Gestion des Agents</h3>
        <button 
          onClick={() => { setEditingAgent(null); setShowAgentModal(true); }}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Ajouter Agent</span>
        </button>
      </div>
      
      {Object.entries(agents).length === 0 && (
        <div className="text-center text-gray-500 py-12">
          <Users size={64} className="mx-auto mb-4 opacity-30" />
          <p>Aucun agent configuré</p>
          <p className="text-sm mt-2">Ajoutez des agents pour recevoir les SMS d'alerte</p>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(agents).map(([agentId, agentInfo]) => {
          const disponibilite = agentInfo.disponibilite || {};
          const joursActifs = JOURS.filter(j => disponibilite[j]);
          
          return (
            <Card key={agentId}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{agentInfo.name}</span>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        setEditingAgent({ id: agentId, data: agentInfo });
                        setShowAgentModal(true);
                      }}
                      className="text-blue-500 hover:text-blue-700 p-1"
                      title="Modifier"
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      onClick={() => deleteAgent(agentId, agentInfo.name)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm flex items-center space-x-2">
                    <span className="text-gray-500">📞</span>
                    <span className="font-mono">{agentInfo.phone}</span>
                  </p>
                  
                  <div>
                    <p className="text-sm font-semibold mb-2 text-gray-700">Horaires :</p>
                    {joursActifs.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">Aucun horaire défini — SMS envoyé 24h/24</p>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-2 space-y-1">
                        {JOURS.map(jour => {
                          const h = disponibilite[jour];
                          if (!h) return null;
                          return (
                            <div key={jour} className="flex justify-between text-xs">
                              <span className="capitalize text-gray-600 font-medium">{jour}</span>
                              <span className="text-blue-700 font-semibold">{h.start}h — {h.end}h</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const SOURCE_COLORS = {
  'iFixit':      'bg-orange-100 text-orange-800',
  'Frandroid':   'bg-purple-100 text-purple-800',
  'Phonandroid': 'bg-pink-100 text-pink-800',
  'manuel':      'bg-green-100 text-green-800',
};

const KnowledgeBaseTab = ({ knowledgeBase, setShowKnowledgeModal, setEditingKnowledge, fetchData }) => {
  const [scanning, setScanning] = useState(false);
  const [filterSource, setFilterSource] = useState('Tous');
  const [filterSearch, setFilterSearch] = useState('');

  const sources = ['Tous', 'manuel', ...Array.from(new Set(knowledgeBase.map(i => i.source).filter(Boolean)))];

  const filtered = knowledgeBase.filter(item => {
    const src = item.source || 'manuel';
    const matchSource = filterSource === 'Tous' || src === filterSource;
    const matchSearch = !filterSearch ||
      (item.title || '').toLowerCase().includes(filterSearch.toLowerCase()) ||
      (item.content || '').toLowerCase().includes(filterSearch.toLowerCase());
    return matchSource && matchSearch;
  });

  const deleteKnowledge = async (id) => {
    if (!confirm('Supprimer cette connaissance ?')) return;
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${window.location.origin}/api/chat/knowledge-base/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const scanRSS = async () => {
    setScanning(true);
    const token = localStorage.getItem('admin_token');
    try {
      const resp = await fetch(`${window.location.origin}/api/chat/knowledge-base/scan-rss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (resp.ok) {
        alert(`✅ ${data.message}`);
        fetchData();
      } else {
        alert(`❌ ${data.detail || 'Erreur'}`);
      }
    } catch (e) {
      alert('❌ Erreur réseau');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-2xl font-bold">📚 Base de Connaissances</h3>
        <div className="flex space-x-2">
          <button
            onClick={scanRSS}
            disabled={scanning}
            data-testid="scan-rss-btn"
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
            title="Scanner Frandroid, iFixit, Phonandroid"
          >
            <span className={scanning ? 'animate-spin inline-block' : ''}>📡</span>
            <span>{scanning ? 'Scan...' : 'Scan RSS'}</span>
          </button>
          <button
            onClick={() => { setEditingKnowledge(null); setShowKnowledgeModal(true); }}
            data-testid="add-kb-btn"
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Ajouter</span>
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-gray-50 border rounded-xl p-3 space-y-3">
        {/* Barre de recherche */}
        <div className="relative">
          <input
            type="text"
            placeholder="Rechercher dans la base..."
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            data-testid="kb-search-input"
            className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {filterSearch && (
            <button onClick={() => setFilterSearch('')} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">✕</button>
          )}
        </div>

        {/* Filtres par source */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Source :</span>
          {sources.map(src => {
            const count = src === 'Tous'
              ? knowledgeBase.length
              : knowledgeBase.filter(i => (i.source || 'manuel') === src).length;
            const isActive = filterSource === src;
            const colorClass = SOURCE_COLORS[src] || 'bg-gray-100 text-gray-700';
            return (
              <button
                key={src}
                onClick={() => setFilterSource(src)}
                data-testid={`filter-source-${src}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                  isActive
                    ? `${colorClass} border-current shadow-sm ring-2 ring-offset-1 ring-current`
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                }`}
              >
                {src} <span className="opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Résultats */}
      <p className="text-xs text-gray-400">
        {filtered.length} article{filtered.length !== 1 ? 's' : ''} affiché{filtered.length !== 1 ? 's' : ''}
        {filterSearch || filterSource !== 'Tous' ? ` (filtré sur ${knowledgeBase.length} total)` : ''}
      </p>

      <div className="grid gap-4">
        {filtered.map((item) => {
          const src = item.source || 'manuel';
          const srcColor = SOURCE_COLORS[src] || 'bg-gray-100 text-gray-700';
          return (
            <Card key={item.id || item._id} data-testid="kb-card">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0 pr-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${srcColor}`}>
                        {src}
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-semibold">
                        {item.category || item.type || 'Général'}
                      </span>
                      {item.updated_at && (
                        <span className="text-xs text-gray-400">
                          {new Date(item.updated_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    <h4 className="font-bold text-base leading-snug">
                      {item.title || item.question || '(Sans titre)'}
                    </h4>
                  </div>
                  <div className="flex space-x-2 flex-shrink-0">
                    <button
                      onClick={() => { setEditingKnowledge(item); setShowKnowledgeModal(true); }}
                      className="text-blue-500 hover:text-blue-700 p-1"
                      title="Modifier"
                      data-testid="kb-edit-btn"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => deleteKnowledge(item.id || item._id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Supprimer"
                      data-testid="kb-delete-btn"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed line-clamp-4">
                  {item.content || item.answer || '(Aucun contenu)'}
                </p>
              </CardContent>
            </Card>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center text-gray-500 py-12">
            <HelpCircle size={64} className="mx-auto mb-4 opacity-30" />
            {knowledgeBase.length === 0
              ? <p>Aucun élément dans la base de connaissances</p>
              : <p>Aucun résultat pour ce filtre</p>
            }
            {knowledgeBase.length === 0 && (
              <p className="text-sm mt-2">Ajoutez des FAQ, instructions ou informations pour le bot</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatsTab = ({ stats }) => (
  <div className="space-y-4">
    <h3 className="text-2xl font-bold mb-4">📊 Statistiques du Bot</h3>
    <div className="grid md:grid-cols-3 gap-4">
      <Card>
        <CardContent className="p-6 text-center">
          <MessageCircle size={48} className="mx-auto text-blue-600 mb-2" />
          <p className="text-3xl font-bold">{stats.total_conversations || 0}</p>
          <p className="text-gray-600">Conversations totales</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6 text-center">
          <BarChart3 size={48} className="mx-auto text-green-600 mb-2" />
          <p className="text-3xl font-bold">{stats.converted || 0}</p>
          <p className="text-gray-600">Converties</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-6 text-center">
          <BarChart3 size={48} className="mx-auto text-purple-600 mb-2" />
          <p className="text-3xl font-bold">
            {stats.conversion_rate ? `${stats.conversion_rate.toFixed(1)}%` : '0%'}
          </p>
          <p className="text-gray-600">Taux de conversion</p>
        </CardContent>
      </Card>
    </div>
  </div>
);

// ============ MODAL COMPONENTS ============

// ──────────────────────────────────────────────────────────────────────────────
// Modaux extraits dans /bot/*.jsx pour maintenir ce fichier lisible
// ──────────────────────────────────────────────────────────────────────────────

export default BotManagementComplete;
